import React, { useState, useEffect } from "react";
import { process } from "@progress/kendo-data-query";
import { Grid, GridColumn } from "@progress/kendo-react-grid";
import { getter } from "@progress/kendo-react-common";
import { useNavigate } from "react-router-dom";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import { formatDate } from "@telerik/kendo-intl";
import { mockSupplierInvoices, calculateOutstanding } from "../shared/mockInvoiceData";
import { invoiceStatus } from "../../../constants";
import { Button } from "@progress/kendo-react-buttons";
import {
  PlusIcon,
  DownloadIcon,
  EyeIcon,
  CheckCircleIcon,
  XCircleIcon,
} from "@heroicons/react/outline";

const DATA_ITEM_KEY = "id";
const SELECTED_FIELD = "selected";
const idGetter = getter(DATA_ITEM_KEY);

const SupplierInvoiceList = () => {
  const navigate = useNavigate();
  const [dataState, setDataState] = useState({
    skip: 0,
    take: 20,
    sort: [{ field: "invoiceDate", dir: "desc" }],
    filter: null,
  });
  const [data, setData] = useState([]);
  const [processedData, setProcessedData] = useState([]);
  const [selectedState, setSelectedState] = useState({});
  const _export = React.useRef(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = () => {
    // Using mock data
    const invoicesWithSelection = mockSupplierInvoices.map((item) => ({
      ...item,
      [SELECTED_FIELD]: selectedState[idGetter(item)],
    }));
    setData(invoicesWithSelection);
  };

  useEffect(() => {
    const processed = process(data, dataState);
    setProcessedData(processed);
  }, [data, dataState]);

  const dataStateChange = (e) => {
    setDataState(e.dataState);
  };

  const handleRowClick = (event) => {
    navigate(`/invoice/supplier/${event.dataItem.id}`);
  };

  const excelExport = () => {
    if (_export.current) {
      _export.current.save();
    }
  };

  const handleCreateNew = () => {
    navigate("/invoice/supplier/new");
  };

  // Status cell with color coding
  const StatusCell = (props) => {
    const status = props.dataItem.status;
    const statusConfig = invoiceStatus.find((s) => s.name === status);
    return (
      <td>
        <span
          className="px-2 py-1 text-xs font-semibold rounded-full"
          style={{
            backgroundColor: statusConfig?.color + "20",
            color: statusConfig?.color,
          }}
        >
          {status}
        </span>
      </td>
    );
  };

  // Date cell formatter
  const DateCell = (props) => {
    const value = props.dataItem[props.field];
    return <td>{value ? formatDate(new Date(value), "dd-MMM-yyyy") : "-"}</td>;
  };

  // Currency cell formatter
  const CurrencyCell = (props) => {
    const value = props.dataItem[props.field];
    const currency = props.dataItem.currency;
    return (
      <td className="text-right">
        {currency} {value?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </td>
    );
  };

  // Outstanding amount cell
  const OutstandingCell = (props) => {
    const total = props.dataItem.totalAmount;
    const paid = props.dataItem.paidAmount;
    const outstanding = total - paid;
    const currency = props.dataItem.currency;
    return (
      <td className="text-right">
        <span className={outstanding > 0 ? "text-red-600 font-semibold" : "text-green-600"}>
          {currency} {outstanding.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </span>
      </td>
    );
  };

  // Actions cell
  const ActionsCell = (props) => {
    const invoice = props.dataItem;
    return (
      <td>
        <div className="flex gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/invoice/supplier/${invoice.id}`);
            }}
            className="p-1 text-blue-600 hover:bg-blue-50 rounded"
            title="View Details"
          >
            <EyeIcon className="w-5 h-5" />
          </button>
          {invoice.status === "Pending Approval" && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  alert("Approve functionality - to be implemented with API");
                }}
                className="p-1 text-green-600 hover:bg-green-50 rounded"
                title="Approve"
              >
                <CheckCircleIcon className="w-5 h-5" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  alert("Reject functionality - to be implemented with API");
                }}
                className="p-1 text-red-600 hover:bg-red-50 rounded"
                title="Reject"
              >
                <XCircleIcon className="w-5 h-5" />
              </button>
            </>
          )}
        </div>
      </td>
    );
  };

  // Calculate statistics
  const totalInvoices = data.length;
  const totalOutstanding = calculateOutstanding(data);
  const pendingApproval = data.filter((inv) => inv.status === "Pending Approval").length;
  const overdueInvoices = data.filter((inv) => inv.status === "Overdue").length;

  return (
    <div className="p-6">
      {/* Header Section */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Supplier Invoices</h1>
            <p className="text-sm text-gray-500 mt-1">
              Manage and track supplier invoices for logistics services
            </p>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={excelExport}
              icon="file-excel"
              className="k-button k-button-md k-rounded-md k-button-outline k-button-outline-base"
            >
              <DownloadIcon className="w-4 h-4 mr-2 inline" />
              Export
            </Button>
            <Button
              onClick={handleCreateNew}
              themeColor="primary"
              className="k-button k-button-md k-rounded-md"
            >
              <PlusIcon className="w-4 h-4 mr-2 inline" />
              New Invoice
            </Button>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4 border-l-4 border-blue-500">
            <div className="text-sm font-medium text-gray-600">Total Invoices</div>
            <div className="text-2xl font-bold text-gray-900 mt-1">{totalInvoices}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 border-l-4 border-red-500">
            <div className="text-sm font-medium text-gray-600">Total Outstanding</div>
            <div className="text-2xl font-bold text-red-600 mt-1">
              ${totalOutstanding.toLocaleString("en-US", { minimumFractionDigits: 2 })}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 border-l-4 border-yellow-500">
            <div className="text-sm font-medium text-gray-600">Pending Approval</div>
            <div className="text-2xl font-bold text-yellow-600 mt-1">{pendingApproval}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 border-l-4 border-orange-500">
            <div className="text-sm font-medium text-gray-600">Overdue</div>
            <div className="text-2xl font-bold text-orange-600 mt-1">{overdueInvoices}</div>
          </div>
        </div>
      </div>

      {/* Grid Section */}
      <div className="bg-white rounded-lg shadow">
        <ExcelExport data={data} ref={_export} fileName="Supplier_Invoices.xlsx">
          <Grid
            data={processedData}
            {...dataState}
            onDataStateChange={dataStateChange}
            sortable={true}
            filterable={true}
            pageable={{
              buttonCount: 5,
              info: true,
              pageSizes: [10, 20, 50, 100],
            }}
            onRowClick={handleRowClick}
            style={{ height: "600px", cursor: "pointer" }}
          >
            <GridColumn
              field="invoiceNumber"
              title="Invoice Number"
              width="180"
              filterable={true}
            />
            <GridColumn
              field="supplierName"
              title="Supplier"
              width="250"
              filterable={true}
            />
            <GridColumn
              field="jobNumber"
              title="Job Number"
              width="150"
              filterable={true}
            />
            <GridColumn
              field="poNumber"
              title="PO Number"
              width="150"
              filterable={true}
            />
            <GridColumn
              field="invoiceDate"
              title="Invoice Date"
              width="140"
              cell={DateCell}
              filterable={false}
            />
            <GridColumn
              field="dueDate"
              title="Due Date"
              width="140"
              cell={DateCell}
              filterable={false}
            />
            <GridColumn
              field="status"
              title="Status"
              width="160"
              cell={StatusCell}
              filterable={true}
            />
            <GridColumn
              field="totalAmount"
              title="Total Amount"
              width="150"
              cell={CurrencyCell}
              filterable={false}
            />
            <GridColumn
              field="outstanding"
              title="Outstanding"
              width="150"
              cell={OutstandingCell}
              filterable={false}
            />
            <GridColumn
              field="invoiceType"
              title="Type"
              width="180"
              filterable={true}
            />
            <GridColumn
              title="Actions"
              width="120"
              cell={ActionsCell}
              filterable={false}
              sortable={false}
            />
          </Grid>
        </ExcelExport>
      </div>
    </div>
  );
};

export default SupplierInvoiceList;
